import { href, Link } from 'react-router'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/support.ts'

export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'Support & FAQs - Syrcha Privacy Compliance Scanner',
		description:
			'Get help with Syrcha privacy compliance scanning. Browse FAQs about GDPR, CCPA, and AI Act scans, contact support, and check system status.',
		matches,
	})

function stripHtml(html: string): string {
	return html.replace(/<[^>]*>/g, '').trim()
}

function faqJsonLd() {
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqs.map((f) => ({
			'@type': 'Question',
			name: f.q,
			acceptedAnswer: { '@type': 'Answer', text: stripHtml(f.a) },
		})),
	}
}

export default function SupportRoute() {
	return (
		// The marketing layout owns the single `<main>` landmark.
		<>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd()) }}
			/>
			<Intro />
			<Channels />
			<FAQs />
			<Status />
		</>
	)
}

function Intro() {
	return (
		<section className="relative isolate overflow-hidden bg-[var(--marketing-base)] pt-16 pb-20 sm:pt-24">
			<div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ support</p>
				<h1
					className="mt-4 max-w-3xl font-[family-name:var(--font-display)] text-[44px] leading-[1.02] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[56px]"
					style={{ textWrap: 'balance' }}
				>
					Real humans. Real <span className="accent-underline">replies</span>.
				</h1>
				<div className="mt-8 grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-start">
					<p className="max-w-xl text-[16px] leading-relaxed text-[var(--marketing-gray)]">
						Questions about scans, reports, or billing: most are answered within
						one business day. No chatbots, no ticket purgatory.
					</p>
					<dl className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-[var(--marketing-border)] pt-6 font-[family-name:var(--font-mono)] text-[12px]">
						<div>
							<dt className="text-[var(--marketing-faint)]">Answered by</dt>
							<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
								Engineers
							</dd>
						</div>
						<div>
							<dt className="text-[var(--marketing-faint)]">Response</dt>
							<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
								≤ 1 business day
							</dd>
						</div>
						<div>
							<dt className="text-[var(--marketing-faint)]">Channels</dt>
							<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
								Email · Status
							</dd>
						</div>
						<div>
							<dt className="text-[var(--marketing-faint)]">Region</dt>
							<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
								EU based
							</dd>
						</div>
					</dl>
				</div>
				<div className="mt-10 flex flex-wrap items-center gap-3">
					<a
						href="mailto:support@syrcha.com?subject=Support%20Request"
						className="btn-marketing"
					>
						Email support
						<span aria-hidden className="ml-2">
							→
						</span>
					</a>
					<Link
						to={href('/resources/healthcheck')}
						className="btn-marketing-outline"
					>
						System status
					</Link>
				</div>
			</div>
		</section>
	)
}

function Channels() {
	const items = [
		{
			eyebrow: '01 - general',
			title: 'Product support',
			body: 'Troubleshooting, account help, anything about scans or reports.',
			cta: 'support@syrcha.com',
			href: 'mailto:support@syrcha.com?subject=Support%20Request',
		},
		{
			eyebrow: '02 - security',
			title: 'Security disclosure',
			body: 'Report a vulnerability or request our security overview. See /.well-known/security.txt.',
			cta: 'security@syrcha.com',
			href: 'mailto:security@syrcha.com?subject=Security%20Disclosure',
		},
		{
			eyebrow: '03 - privacy',
			title: 'Privacy + DSAR',
			body: 'Data subject access requests, deletion, or questions about our processing.',
			cta: 'privacy@syrcha.com',
			href: 'mailto:privacy@syrcha.com?subject=Privacy%20Request',
		},
		{
			eyebrow: '04 - sales',
			title: 'Plans + invoicing',
			body: 'Procurement paperwork, agency volume, custom invoicing.',
			cta: 'sales@syrcha.com',
			href: 'mailto:sales@syrcha.com?subject=Sales%20Inquiry',
		},
	]
	return (
		<section className="relative border-t border-[var(--marketing-border)] bg-[var(--marketing-surface)] py-24">
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<header className="grid gap-8 md:grid-cols-[1.3fr_1fr] md:items-end">
					<div>
						<p className="eyebrow text-[var(--marketing-faint)]">/ channels</p>
						<h2
							className="mt-4 font-[family-name:var(--font-display)] text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
							style={{ textWrap: 'balance' }}
						>
							Pick the address that{' '}
							<span className="accent-underline">fits</span>.
						</h2>
					</div>
					<p className="text-[15px] leading-relaxed text-[var(--marketing-gray)]">
						Routed to a small team, not a queue. All four reach a real inbox.
					</p>
				</header>

				<ul className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-[var(--marketing-border)] bg-[var(--marketing-border)] md:grid-cols-2">
					{items.map((it) => (
						<li
							key={it.title}
							className="bg-[var(--marketing-base)] transition-colors hover:bg-[var(--marketing-elevated)]"
						>
							<a
								href={it.href}
								className="group flex h-full flex-col justify-between gap-6 p-8"
							>
								<p className="font-[family-name:var(--font-mono)] text-[11px] tracking-[0.2em] text-[var(--marketing-faint)] uppercase">
									{it.eyebrow}
								</p>
								<div className="space-y-3">
									<h3 className="font-[family-name:var(--font-display)] text-[22px] font-semibold tracking-[-0.01em] text-[var(--marketing-black)]">
										{it.title}
									</h3>
									<p className="text-[14px] leading-relaxed text-[var(--marketing-gray)]">
										{it.body}
									</p>
								</div>
								<span className="inline-flex items-center gap-2 font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-accent)] transition-transform group-hover:translate-x-0.5">
									{it.cta}
									<span aria-hidden>→</span>
								</span>
							</a>
						</li>
					))}
				</ul>
			</div>
		</section>
	)
}

function FAQs() {
	return (
		<section className="relative border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-24">
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<header className="max-w-2xl">
					<p className="eyebrow text-[var(--marketing-faint)]">/ faq</p>
					<h2
						className="mt-4 font-[family-name:var(--font-display)] text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
						style={{ textWrap: 'balance' }}
					>
						Asked often, answered{' '}
						<span className="accent-underline">in full</span>.
					</h2>
					<p className="mt-4 text-[15px] leading-relaxed text-[var(--marketing-gray)]">
						No accordions for the sake of accordions. Everything visible at
						once: scan, find what you need.
					</p>
				</header>

				<dl className="mt-14 divide-y divide-[var(--marketing-border)] border-y border-[var(--marketing-border)]">
					{faqs.map((f, i) => (
						<div
							key={f.q}
							className="grid gap-6 py-7 md:grid-cols-[200px_1fr] md:gap-12"
						>
							<dt className="flex items-start gap-3 font-[family-name:var(--font-display)] text-[16px] font-semibold tracking-tight text-[var(--marketing-black)]">
								<span className="font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-accent)]">
									{String(i + 1).padStart(2, '0')}
								</span>
								<span style={{ textWrap: 'balance' }}>{f.q}</span>
							</dt>
							<dd
								className="text-[14px] leading-relaxed text-[var(--marketing-gray)] [&_a]:text-[var(--marketing-accent)] [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-[var(--marketing-accent-light)] [&_li]:mt-1 [&_strong]:text-[var(--marketing-black)] [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5"
								dangerouslySetInnerHTML={{ __html: f.a }}
							/>
						</div>
					))}
				</dl>

				<div className="mt-12 flex flex-col items-start gap-4 rounded-xl border border-[var(--marketing-border)] bg-[var(--marketing-elevated)] p-6 sm:flex-row sm:items-center sm:justify-between">
					<p className="text-[14px] text-[var(--marketing-gray)]">
						Question not on the list?{' '}
						<a
							className="text-[var(--marketing-accent)] underline underline-offset-4 hover:text-[var(--marketing-accent-light)]"
							href="mailto:support@syrcha.com"
						>
							support@syrcha.com
						</a>
						. We read every one.
					</p>
					<a
						href="mailto:support@syrcha.com?subject=Support%20Request"
						className="btn-marketing-outline"
					>
						Email us
					</a>
				</div>
			</div>
		</section>
	)
}

function Status() {
	return (
		<section className="relative border-t border-[var(--marketing-border)] bg-[var(--marketing-surface)] py-24">
			<div className="relative z-10 mx-auto max-w-3xl px-6 text-center lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ status</p>
				<h2
					className="mx-auto mt-4 max-w-xl font-[family-name:var(--font-display)] text-[38px] leading-[1.04] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
					style={{ textWrap: 'balance' }}
				>
					Something feel off?{' '}
					<span className="accent-underline">Check live status</span>.
				</h2>
				<p className="mx-auto mt-5 max-w-lg text-[15px] leading-relaxed text-[var(--marketing-gray)]">
					Scanner and API health, latest incidents, and historical uptime, all
					on one page.
				</p>
				<div className="mt-8 flex flex-wrap items-center justify-center gap-3">
					<Link to={href('/resources/healthcheck')} className="btn-marketing">
						Open status page
						<span aria-hidden className="ml-2">
							→
						</span>
					</Link>
					<a
						href="mailto:support@syrcha.com?subject=Incident%20Report"
						className="btn-marketing-outline"
					>
						Report an incident
					</a>
				</div>
			</div>
		</section>
	)
}

const faqs = [
	{
		q: 'What does Syrcha scan for?',
		a: `Syrcha drives a real headless Chromium instance and a configurable rules engine against your public site. It checks for: <ul><li>Cookie consent banners and whether tracking starts before consent</li><li>Privacy policy reachability and jurisdictional disclosures (e.g. California rights)</li><li>Explicit consent patterns on forms</li><li>AI-related disclosures for chatbots and recommenders</li></ul>`,
	},
	{
		q: 'Which compliance frameworks are supported?',
		a: 'GDPR rules are live. CCPA is in beta. The EU AI Act ruleset ships Q3 2026. The rule engine is extensible; additional frameworks are tracked in the public roadmap.',
	},
	{
		q: 'How are scores calculated?',
		a: 'Each rule carries a weight. Failures subtract from the base score; warnings subtract half. You get an overall score plus a per-framework score so prioritisation is obvious.',
	},
	{
		q: 'How often can I scan?',
		a: 'Plan dependent: Starter is weekly, Growth is daily, Agency is unlimited. Manual scans are always available from the dashboard; we email when each scan finishes.',
	},
	{
		q: 'Do you store data from my site?',
		a: 'We analyse public pages and store the resulting scan output, metadata, and reports. We do not store raw page bodies beyond what is needed to produce findings. See the <a href="/privacy">Privacy Policy</a> for specifics.',
	},
	{
		q: 'Can I cancel any time?',
		a: 'Yes. Cancel from settings without sending an email. Your reports remain available until you choose to delete them.',
	},
]
